/**
 * Node visual para criar negócio CRM no Flow Builder
 * Exibe configuração de workspace, pipeline, fase, origem e vendedor
 * 
 * RELEASE: 1.0.0 - 2025-01-15 - Novo node CRM
 * RELEASE: 2.0.0 - 2025-01-15 - Adicionado indicadores de verificação de duplicidade
 */

import { Handle, Position, NodeProps } from '@xyflow/react';
import { Briefcase, User, Users, MapPin, Shield, Clock, AlertTriangle, Package } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { FlowNodeData, CrmCreateDealConfig } from '@/types/flows';
import { FlowNodeToolbar } from './FlowNodeToolbar';
import { getLeadSourceIcon, getLeadSourceLabel } from '@/lib/lead-sources';

/**
 * Traduz o comportamento de duplicidade para texto legível
 */
const getDuplicateBehaviorLabel = (behavior?: string): string => {
  switch (behavior) {
    case 'always_create':
      return 'Sempre criar';
    case 'skip_if_open':
      return 'Ignorar se aberto';
    case 'update_phase':
      return 'Atualizar fase';
    case 'create_if_all_closed':
      return 'Criar se fechados';
    default:
      return 'Ignorar se aberto';
  }
};

/**
 * Traduz a unidade de tempo para texto legível
 */
const getWindowUnitLabel = (unit?: string, value?: number): string => {
  const plural = value !== 1;
  switch (unit) {
    case 'days':
      return plural ? 'dias' : 'dia';
    case 'weeks':
      return plural ? 'semanas' : 'semana';
    case 'months':
      return plural ? 'meses' : 'mês';
    default:
      return 'dias';
  }
};

/**
 * Componente visual do node Criar Negócio CRM
 * Exibe hierarquia Workspace > Pipeline > Fase e informações adicionais
 */
export function CrmCreateDealNode({ data, id, selected }: NodeProps) {
  const nodeData = data as FlowNodeData;
  const config = nodeData.config as CrmCreateDealConfig | undefined;
  
  // Verifica se node está configurado
  const isConfigured = config?.phase_id && config?.source;
  
  // Obtém ícone da origem
  const SourceIcon = config?.source ? getLeadSourceIcon(config.source) : null;
  const sourceLabel = config?.source ? getLeadSourceLabel(config.source) : null;
  
  // Determina texto do modo de título
  const getTitleModeLabel = () => {
    if (!config?.title_mode) return null;
    switch (config.title_mode) {
      case 'auto':
        return 'Automático (nome + data)';
      case 'static':
        return config.title_static || 'Texto fixo';
      case 'from_variable':
        return `{{${config.title_variable || 'variavel'}}}`;
      default:
        return null;
    }
  };

  // Monta resumo do escopo de verificação
  const getScopeDescription = (): string | null => {
    if (!config?.duplicate_check_enabled) return null;
    
    const parts: string[] = [];
    
    if (config.check_workspace_names && config.check_workspace_names.length > 0) {
      parts.push(`${config.check_workspace_names.length} workspace${config.check_workspace_names.length > 1 ? 's' : ''}`);
    }
    if (config.check_pipeline_names && config.check_pipeline_names.length > 0) {
      parts.push(`${config.check_pipeline_names.length} pipeline${config.check_pipeline_names.length > 1 ? 's' : ''}`);
    }
    if (config.check_phase_names && config.check_phase_names.length > 0) {
      parts.push(`${config.check_phase_names.length} fase${config.check_phase_names.length > 1 ? 's' : ''}`);
    }
    
    return parts.length > 0 ? parts.join(', ') : 'Todo o CRM';
  };

  return (
    <>
      {/* Toolbar flutuante para edição/exclusão */}
      <FlowNodeToolbar
        nodeId={id}
        nodeType={nodeData.type}
        onEdit={nodeData.onEdit as (() => void) | undefined}
        onDuplicate={(nodeData as any).onDuplicate as (() => void) | undefined}
        onDelete={nodeData.onDelete as (() => void) | undefined}
        onTestFromHere={(nodeData as any).onTestFromHere as (() => void) | undefined}
        isVisible={selected}
      />
      
      {/* Handle de entrada (topo) */}
      <Handle
        type="target"
        position={Position.Top}
        className="!w-3 !h-3 !bg-violet-500 !border-2 !border-white"
      />

      {/* Corpo do node */}
      <div className={`
        min-w-[220px] max-w-[280px] bg-card rounded-lg border-2 shadow-lg
        ${selected ? 'border-violet-500 ring-2 ring-violet-500/30' : 'border-violet-400'}
        transition-all duration-200
      `}>
        {/* Header */}
        <div className="flex items-center gap-2 p-3 border-b border-border bg-violet-500/10 rounded-t-lg">
          <div className="p-1.5 rounded bg-violet-500">
            <Briefcase className="h-4 w-4 text-white" />
          </div>
          <span className="font-semibold text-sm">{nodeData.config?.nickname || 'Criar Negócio'}</span>
          {/* Indicador de verificação ativa */}
          {config?.duplicate_check_enabled && (
            <span title="Verificação de duplicidade ativa">
              <Shield className="h-3.5 w-3.5 text-emerald-500 ml-auto" />
            </span>
          )}
        </div>

        {/* Conteúdo */}
        <div className="p-3 space-y-2 text-xs">
          {isConfigured ? (
            <>
              {/* Hierarquia: Workspace > Pipeline > Fase */}
              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <MapPin className="h-3 w-3" />
                  <span className="font-medium">Destino:</span>
                </div>
                <div className="pl-4 space-y-0.5">
                  <p className="truncate" title={config.workspace_name}>
                    <span className="text-muted-foreground">Workspace:</span>{' '}
                    <span className="font-medium">{config.workspace_name}</span>
                  </p>
                  <p className="truncate" title={config.pipeline_name}>
                    <span className="text-muted-foreground">Pipeline:</span>{' '}
                    <span className="font-medium">{config.pipeline_name}</span>
                  </p>
                  <div className="flex items-center gap-1.5">
                    <span className="text-muted-foreground">Fase:</span>
                    {config.phase_color && (
                      <span 
                        className="w-2.5 h-2.5 rounded-full inline-block"
                        style={{ backgroundColor: config.phase_color }}
                      />
                    )}
                    <span className="font-medium truncate" title={config.phase_name}>
                      {config.phase_name}
                    </span>
                  </div>
                </div>
              </div>

              {/* Origem */}
              <div className="flex items-center gap-1.5 pt-1 border-t border-border/50">
                {SourceIcon && <SourceIcon className="h-3 w-3 text-muted-foreground" />}
                <span className="text-muted-foreground">Origem:</span>
                <span className="font-medium">{sourceLabel}</span>
              </div>

              {/* Vendedor ou Fila de Distribuição */}
              {config?.seller_assignment_mode === 'manual' && config?.seller_name && (
                <div className="flex items-center gap-1.5">
                  <User className="h-3 w-3 text-muted-foreground" />
                  <span className="text-muted-foreground">Vendedor:</span>
                  <span className="font-medium truncate" title={config.seller_name}>
                    {config.seller_name}
                  </span>
                </div>
              )}
              
              {config?.seller_assignment_mode === 'distribution_queue' && config?.distribution_queue_name && (
                <div className="flex items-center gap-1.5">
                  <Users className="h-3 w-3 text-muted-foreground" />
                  <span className="text-muted-foreground">Fila:</span>
                  <span className="font-medium truncate" title={config.distribution_queue_name}>
                    {config.distribution_queue_name}
                  </span>
                  <Badge variant="secondary" className="text-[10px] px-1 py-0">
                    {config.distribution_queue_method === 'round_robin' && 'RR'}
                    {config.distribution_queue_method === 'least_busy' && 'LB'}
                    {config.distribution_queue_method === 'weighted' && 'W'}
                  </Badge>
                </div>
              )}
              
              {/* Vendedor legado (quando seller_assignment_mode não definido) */}
              {(!config?.seller_assignment_mode || config?.seller_assignment_mode === undefined) && config?.seller_name && (
                <div className="flex items-center gap-1.5">
                  <User className="h-3 w-3 text-muted-foreground" />
                  <span className="text-muted-foreground">Vendedor:</span>
                  <span className="font-medium truncate" title={config.seller_name}>
                    {config.seller_name}
                  </span>
                </div>
              )}

              {/* Indicador de conversa vinculada ao vendedor (v4.0.0) */}
              {config?.assign_conversation_to_seller && config?.seller_assignment_mode !== 'none' && (
                <div className="flex items-center gap-1.5 text-blue-600 dark:text-blue-400">
                  <User className="h-3 w-3" />
                  <span className="text-[10px] font-medium">Conversa vinculada ao vendedor</span>
                </div>
              )}

              {/* Modo do título */}
              {getTitleModeLabel() && (
                <div className="flex items-center gap-1.5 pt-1 border-t border-border/50">
                  <span className="text-muted-foreground">Título:</span>
                  <span className="font-medium truncate" title={getTitleModeLabel() || ''}>
                    {getTitleModeLabel()}
                  </span>
                </div>
              )}

              {/* Verificação de Duplicidade (se habilitada) */}
              {config.duplicate_check_enabled && (
                <div className="pt-1.5 mt-1.5 border-t border-emerald-500/30 space-y-1">
                  <div className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400">
                    <Shield className="h-3 w-3" />
                    <span className="font-medium text-[10px] uppercase tracking-wide">Verificação Ativa</span>
                  </div>
                  
                  {/* Escopo */}
                  {getScopeDescription() && (
                    <div className="flex items-center gap-1 text-muted-foreground pl-4">
                      <AlertTriangle className="h-2.5 w-2.5" />
                      <span className="text-[10px]">Escopo: {getScopeDescription()}</span>
                    </div>
                  )}
                  
                  {/* Janela de tempo */}
                  {config.recurrence_window_value && config.recurrence_window_value > 0 && (
                    <div className="flex items-center gap-1 text-muted-foreground pl-4">
                      <Clock className="h-2.5 w-2.5" />
                      <span className="text-[10px]">
                        Janela: {config.recurrence_window_value} {getWindowUnitLabel(config.recurrence_window_unit, config.recurrence_window_value)}
                      </span>
                    </div>
                  )}
                  
                  {/* Comportamento */}
                  <div className="flex items-center gap-1 text-muted-foreground pl-4">
                    <span className="text-[10px]">
                      Se existir: <span className="font-medium">{getDuplicateBehaviorLabel(config.duplicate_behavior)}</span>
                    </span>
                  </div>
                </div>
              )}

              {/* Indicador de Produtos pré-configurados (v5.0.0) */}
              {config.products && config.products.length > 0 && (
                <div className="flex items-center gap-1.5 pt-1 border-t border-border/50">
                  <Package className="h-3 w-3 text-amber-500" />
                  <span className="text-muted-foreground">Produtos:</span>
                  <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                    {config.products.length} {config.products.length === 1 ? 'item' : 'itens'}
                  </Badge>
                </div>
              )}

              {/* Variável de saída (se configurada) */}
              {config.output_variable && (
                <div className="flex items-center gap-1.5 text-violet-600 dark:text-violet-400 pt-1 border-t border-border/50">
                  <span>→ Salva ID em:</span>
                  <code className="bg-violet-100 dark:bg-violet-900/30 px-1 rounded text-[10px]">
                    {`{{${config.output_variable}}}`}
                  </code>
                </div>
              )}
              
              {/* Variável de resultado de duplicidade (se configurada) */}
              {config.duplicate_result_variable && config.duplicate_check_enabled && (
                <div className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400 pl-4">
                  <span className="text-[10px]">
                    → Resultado em:{' '}
                    <code className="bg-emerald-100 dark:bg-emerald-900/30 px-1 rounded">
                      {`{{${config.duplicate_result_variable}}}`}
                    </code>
                  </span>
                </div>
              )}
            </>
          ) : (
            <p className="text-muted-foreground italic text-center py-2">
              Clique para configurar o negócio
            </p>
          )}
        </div>
      </div>

      {/* Handle de saída (base) */}
      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-3 !h-3 !bg-violet-500 !border-2 !border-white"
      />
    </>
  );
}
